import { styled } from "goober";
import type { CSSProperties } from "react";
import { useMakeUrl, routes } from "../lib/urls";
import { CircleMesh } from "./CircleMesh";
import { parseBadgeConfig } from "./CircleMeshEditor";
import { Icon } from "./Icon";
import { PreviewListBox } from "./PreviewList";
import { Text } from "./Text";

export type WorkflowPreview = {
  id: string;
  name: string;
  badgeSeed: string | null;
  spaceId: string;
  draft?: boolean;
};

const WorkflowLink = styled("a")`
  all: unset;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  min-width: 0;

  & > span:not(.arrow-icon) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
  }

  &:hover {
    background: var(--alpha4);
  }

  & .arrow-icon {
    opacity: 0;
    flex-shrink: 0;
    display: flex;
    margin-left: auto;
  }

  &:hover .arrow-icon {
    opacity: 0.5;
  }
`;

const WorkflowBadge = styled("div")`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  border-radius: 4px;
  position: relative;

  & > div:first-child {
    width: 16px;
    height: 16px;
    border-radius: 4px;
    overflow: hidden;
  }
`;

const blueprintOverlayStyle: CSSProperties = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  pointerEvents: "none",
  overflow: "visible",
};

function BlueprintOverlay() {
  return (
    <svg
      style={blueprintOverlayStyle}
      viewBox="0 0 2 2"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="0"
        y="0"
        width="2"
        height="2"
        rx="0.5"
        ry="0.5"
        stroke="var(--text-secondary)"
        strokeOpacity={1}
        strokeWidth="0.0625"
      />
      <line
        x1="0.5"
        y1="-0.1875"
        x2="0.5"
        y2="2.1875"
        stroke="var(--text-secondary)"
        strokeOpacity={1}
        strokeWidth="0.0625"
      />
      <line
        x1="1.5"
        y1="-0.1875"
        x2="1.5"
        y2="2.1875"
        stroke="var(--text-secondary)"
        strokeOpacity={1}
        strokeWidth="0.0625"
      />
      <line
        x1="-0.1875"
        y1="0.5"
        x2="2.1875"
        y2="0.5"
        stroke="var(--text-secondary)"
        strokeOpacity={1}
        strokeWidth="0.0625"
      />
      <line
        x1="-0.1875"
        y1="1.5"
        x2="2.1875"
        y2="1.5"
        stroke="var(--text-secondary)"
        strokeOpacity={1}
        strokeWidth="0.0625"
      />
      <circle
        cx="0.5"
        cy="0.5"
        r="0.5"
        stroke="var(--text-secondary)"
        strokeOpacity={1}
        strokeWidth="0.0625"
      />
      <circle
        cx="1.5"
        cy="0.5"
        r="0.5"
        stroke="var(--text-secondary)"
        strokeOpacity={1}
        strokeWidth="0.0625"
      />
      <circle
        cx="0.5"
        cy="1.5"
        r="0.5"
        stroke="var(--text-secondary)"
        strokeOpacity={1}
        strokeWidth="0.0625"
      />
      <circle
        cx="1.5"
        cy="1.5"
        r="0.5"
        stroke="var(--text-secondary)"
        strokeOpacity={1}
        strokeWidth="0.0625"
      />
    </svg>
  );
}

export function WorkflowRow({ workflow }: { workflow: WorkflowPreview }) {
  const makeUrl = useMakeUrl();
  const badgeConfig = parseBadgeConfig(workflow.badgeSeed, workflow.id);
  const draft = workflow.draft === true;
  return (
    <WorkflowLink
      href={makeUrl(routes.space.workflow, {
        spaceId: workflow.spaceId,
        workflowId: workflow.id,
      })}
      target="_blank"
      rel="noopener noreferrer"
    >
      <WorkflowBadge>
        <div style={draft ? { opacity: 0.5 } : undefined}>
          <CircleMesh
            key={workflow.badgeSeed ?? workflow.id}
            targetCircleSize={8}
            seed={badgeConfig.seed}
            mutingIntensity={0}
            animate={false}
            restrictedColors={badgeConfig.colors}
          />
        </div>
        {draft && <BlueprintOverlay />}
      </WorkflowBadge>
      <Text variant="text-regular" color="var(--text-primary)" ellipsis>
        {workflow.name}
      </Text>
      <span className="arrow-icon">
        <Icon name="arrow-up-right" size={12} />
      </span>
    </WorkflowLink>
  );
}

export function WorkflowPreviewList({
  workflows,
}: {
  workflows: readonly WorkflowPreview[];
}) {
  if (workflows.length === 0) return null;
  return (
    <PreviewListBox>
      {workflows.map((workflow) => (
        <WorkflowRow key={workflow.id} workflow={workflow} />
      ))}
    </PreviewListBox>
  );
}
